/**
 * Retry Utilities for Keywords AI Operations
 * 
 * Provides retry logic with exponential backoff for network operations.
 * Used by PromptService and KeywordsPushService to handle transient failures.
 * 
 * ## Usage
 * 
 * ```typescript
 * import { withRetry } from './retryUtils';
 * 
 * const result = await withRetry(
 *     async () => fetch('https://api.example.com/data'),
 *     { maxAttempts: 3, delayMs: 1000 }
 * );
 * ```
 */

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface RetryConfig {
    /** Maximum number of attempts (including initial attempt). Default: 3 */
    maxAttempts: number;
    
    /** Initial delay between retries in milliseconds. Default: 1000 (1 second) */
    delayMs: number;
    
    /** Backoff multiplier for exponential backoff. Default: 2 (doubles each retry) */
    backoffMultiplier: number;
    
    /** Optional custom error handler to determine if error is retryable */
    isRetryable?: (error: Error) => boolean;
    
    /** Optional callback for each retry attempt */
    onRetry?: (attempt: number, error: Error) => void;
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxAttempts: 3,
    delayMs: 1000,
    backoffMultiplier: 2,
};

/**
 * Result of a retry operation
 */
export interface RetryResult<T> {
    success: boolean;
    result?: T;
    error?: Error;
    attempts: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// RETRY UTILITY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Execute an async operation with retry logic and exponential backoff.
 * 
 * @template T - The return type of the operation
 * @param operation - The async operation to execute
 * @param config - Optional retry configuration
 * @returns The result of the operation
 * @throws The last error if all attempts fail
 * 
 * @example
 * // Basic usage with defaults (3 attempts, 1s/2s/4s delays)
 * const data = await withRetry(() => fetchData());
 * 
 * @example
 * // Custom config with 5 attempts and 500ms initial delay
 * const data = await withRetry(
 *     () => fetchData(),
 *     { maxAttempts: 5, delayMs: 500 }
 * );
 * 
 * @example
 * // With retry callback for logging
 * const data = await withRetry(
 *     () => fetchData(),
 *     { 
 *         maxAttempts: 3,
 *         onRetry: (attempt, error) => {
 *             console.log(`Retry attempt ${attempt}: ${error.message}`);
 *         }
 *     }
 * );
 */
export async function withRetry<T>(
    operation: () => Promise<T>,
    config?: Partial<RetryConfig>
): Promise<T> {
    const cfg: RetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    
    let lastError: Error | undefined;
    let currentDelay = cfg.delayMs;
    
    for (let attempt = 1; attempt <= cfg.maxAttempts; attempt++) {
        try {
            console.log(`[withRetry] Attempt ${attempt}/${cfg.maxAttempts}...`);
            const result = await operation();
            
            if (attempt > 1) {
                console.log(`[withRetry] ✅ Succeeded on attempt ${attempt}`);
            }
            
            return result;
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            
            console.warn(`[withRetry] ❌ Attempt ${attempt}/${cfg.maxAttempts} failed:`, lastError.message);
            
            // Check if error is retryable (if custom handler provided)
            if (cfg.isRetryable && !cfg.isRetryable(lastError)) {
                console.log(`[withRetry] Error is not retryable, stopping`);
                throw lastError;
            }
            
            // If this was the last attempt, throw
            if (attempt >= cfg.maxAttempts) {
                console.error(`[withRetry] ❌ All ${cfg.maxAttempts} attempts failed`);
                throw lastError;
            }
            
            // Call retry callback if provided
            if (cfg.onRetry) {
                cfg.onRetry(attempt, lastError);
            }
            
            // Wait before next attempt (exponential backoff)
            console.log(`[withRetry] Waiting ${currentDelay}ms before retry ${attempt + 1}...`);
            await sleep(currentDelay);
            currentDelay *= cfg.backoffMultiplier;
        }
    }
    
    // Should never reach here, but TypeScript needs this
    throw lastError || new Error('Retry failed for unknown reason');
}

// REMOVED: tryWithRetry - no callers (withRetry is used instead)

/**
 * Check if an HTTP error is retryable (5xx errors, timeouts, network failures)
 * 
 * @param error - The error to check
 * @returns true if the error is likely transient and should be retried
 */
export function isRetryableHttpError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    
    // Network errors (transient)
    if (errorMessage.includes('fetch') || 
        errorMessage.includes('network') || 
        errorMessage.includes('timeout') ||
        errorMessage.includes('aborted')) {
        return true;
    }
    
    // Check for HTTP status in error message
    // 5xx errors are server errors (retryable)
    // 4xx errors are client errors (not retryable - auth, bad request, etc.)
    const statusMatch = errorMessage.match(/status[:\s]+(\d{3})/i);
    if (statusMatch) {
        const status = parseInt(statusMatch[1], 10);
        
        // 500-599: Server errors (retry)
        // 429: Rate limit (retry with backoff)
        if (status >= 500 || status === 429) {
            return true;
        }
        
        // 400-499 (except 429): Client errors (don't retry)
        if (status >= 400 && status < 500) {
            return false;
        }
    }
    
    // Default: retry (optimistic)
    return true;
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default withRetry;
